import type { StoreData } from "@features/stores/types";
import { isEmpty } from "lodash-es";
import type { Cart } from "@graphql/generated/types";
import type { mapClickAndCollectStoreToDeliveryAddress } from "@features/checkout/components/delivery-step/utils/mapPickupPointToAddress";
import { ShippingMethodTypes } from "@features/checkout/types";
import { getDeliveryMethodKey } from "@lib/utils/findDeliveryMethods";

/**
 * Validates that the persisted store data still exists in the list of available stores.
 * If the store exists, returns the updated store data.
 * If the store doesn't exist, returns null.
 *
 * @param persistedStoreData - The store data from persisted state (e.g., from Zustand)
 * @param availableStores - The list of currently available stores fetched from the API
 * @returns The validated/updated store data or null if the store no longer exists
 */
export const validateStoreData = (
    persistedStoreData: StoreData | null | undefined,
    availableStores: StoreData[]
): StoreData | null => {
    if (!persistedStoreData || isEmpty(persistedStoreData)) {
        return null;
    }

    if (!availableStores || availableStores.length === 0) {
        return null;
    }

    const currentStore = availableStores.find(
        (store) => store.storeId === persistedStoreData.storeId
    );

    if (!currentStore) {
        return null;
    }

    return currentStore;
};

export const isClickAndCollectAddressValid = (
    cart: Pick<Cart, "shippingInfo" | "shippingAddress" | "lineItems">,
    selectedStore?: StoreData,
    expectedAddress?: ReturnType<typeof mapClickAndCollectStoreToDeliveryAddress>
): boolean => {
    if (
        cart.shippingInfo?.shippingMethod?.key !==
        getDeliveryMethodKey(ShippingMethodTypes.CLICK_AND_COLLECT)
    ) {
        return true;
    }

    const supplyChannelId = selectedStore?.supplyChannel?.[0]?.id;
    if (
        !selectedStore?.storeId ||
        !supplyChannelId ||
        !cart.lineItems?.length ||
        !cart.lineItems.every((lineItem) => lineItem.supplyChannel?.id === supplyChannelId) ||
        !cart.shippingAddress ||
        !expectedAddress
    ) {
        return false;
    }

    const addressFields = [
        "streetName",
        "city",
        "postalCode",
        "region",
        "country",
        "additionalAddressInfo",
    ] as const;
    const normalize = (value: unknown, field: (typeof addressFields)[number]): string => {
        const normalized =
            typeof value === "string" ? value.trim().replace(/\s+/g, " ").toLowerCase() : "";
        return field === "postalCode" ? normalized.replace(/[\s-]/g, "") : normalized;
    };

    return addressFields.every(
        (field) =>
            normalize(cart.shippingAddress[field], field) ===
            normalize(expectedAddress[field], field)
    );
};
